import * as React from "react";
import { useMenuStore, MenuButton, Menu } from "@ariakit/react/menu";
import { Icon, IconNames } from "@nypl/design-system-react-components";
import Account from "../icons/Account";
import Copy from "../icons/Copy";
import { SignOut } from "./SignOut";
import useUser from "./context/UserContext";
import { copyToClipboard } from "../utils/clipboard";
import Button from "./Button";
import { styleProps } from "./Button/styles";
import { useTranslation } from "next-i18next/pages";

export const AccountMenu: React.FC = () => {
  const { t } = useTranslation();
  const menu = useMenuStore();
  const { patronId } = useUser();

  return (
    <>
      <MenuButton
        store={menu}
        sx={{
          ...styleProps("ui.black", "md", "ghost"),
          display: "flex",
          alignItems: "center",
          gap: 1
        }}
      >
        <Account sx={{ width: 20, height: 20 }} />
        {t("accountMenu.account", "Account")}
      </MenuButton>

      <Menu
        store={menu}
        gutter={8}
        tabIndex={-1}
        sx={{
          backgroundColor: "white",
          borderRadius: 2,
          boxShadow: "modal",
          border: "1px solid",
          borderColor: "ui.gray.light",
          py: 0,
          minWidth: "420px",
          zIndex: "headerMenu",
          display: "flex",
          flexDirection: "column"
        }}
      >
        {/* Close button header */}
        <div
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            p: 2
          }}
        >
          <Button
            variant="ghost"
            color="ui.gray.dark"
            onClick={menu.hide}
            sx={{
              minWidth: "auto",
              p: 1
            }}
            aria-label={t("accountMenu.closeMenu", "Close account menu")}
          >
            <Icon
              decorative={false}
              name={IconNames.close}
              sx={{ fontSize: 18 }}
            />
          </Button>
        </div>

        {/* Patron ID row (only if available) */}
        {patronId && <PatronIdMenuItem patronId={patronId} />}

        {/* Sign Out */}
        <SignOutMenuItem />
      </Menu>
    </>
  );
};

interface PatronIdMenuItemProps {
  patronId: string;
}

const PatronIdMenuItem: React.FC<PatronIdMenuItemProps> = ({ patronId }) => {
  const { t } = useTranslation();
  const [copyStatus, setCopyStatus] = React.useState<
    "idle" | "copied" | "error"
  >("idle");

  const handleCopy = async () => {
    const success = await copyToClipboard(patronId);
    setCopyStatus(success ? "copied" : "error");
    setTimeout(() => setCopyStatus("idle"), 2000);
  };

  return (
    <div sx={{ px: 3, py: 2 }} data-testid="patron-id-menuitem">
      <button
        onClick={handleCopy}
        type="button"
        sx={{
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 2,
          backgroundColor: "transparent",
          border: "none",
          width: "100%",
          textAlign: "left",
          color: "ui.black",
          px: 2,
          py: 2,
          fontFamily: "body",
          fontSize: 1,
          fontWeight: "regular",
          borderRadius: 1,
          "&:hover": {
            backgroundColor: "ui.gray.lightWarm"
          },
          "&:focus": {
            outline: "2px solid",
            outlineColor: "brand.primary",
            outlineOffset: "-2px"
          }
        }}
      >
        <span sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
          {t("accountMenu.patronId", "Patron ID: {{patronId}}", { patronId })}
        </span>
        <div
          sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}
        >
          {copyStatus === "copied" && (
            <span
              sx={{ fontSize: 0 }}
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              {t("accountMenu.copied", "Copied!")}
            </span>
          )}
          {copyStatus === "error" && (
            <span
              sx={{ fontSize: 0, color: "ui.error" }}
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              {t("accountMenu.failed", "Failed")}
            </span>
          )}
          <Copy sx={{ width: 16, height: 16 }} />
        </div>
      </button>
    </div>
  );
};

const SignOutMenuItem: React.FC = () => {
  // Reuse the existing SignOut component within the menu
  // SignOut already has the DialogDisclosure button and Modal
  return (
    <div
      sx={{
        px: 3,
        py: 2,
        "& > button": {
          width: "100%",
          justifyContent: "flex-start",
          px: 2,
          py: 2,
          fontWeight: "regular",
          borderRadius: 1
        }
      }}
    >
      <SignOut color="ui.black" />
    </div>
  );
};
